import React, { useMemo, useState } from 'react';
import ChartOfAccountsSearch from './ChartOfAccountsSearch';
import { formatCurrency } from '../utils/numberUtils';

const PercentageTaxEditModal = ({ record, onClose, onSave }) => {
  const taxRates = useMemo(
    () => [
      { value: 1, label: '1%' },
      { value: 2, label: '2%' },
      { value: 3, label: '3% (default)' },
      { value: 5, label: '5%' },
      { value: 10, label: '10%' },
    ],
    []
  );

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: new Date(0, i).toLocaleString('en-US', { month: 'short' }),
      })),
    []
  );

  const isIncome = record.category === 'income';

  const [form, setForm] = useState({
    _id: record._id,
    month: record.month || '',
    chartOfAccount: record.chartOfAccount || null,
    chartOfAccountName: record.chartOfAccountName || '',
    amount: record.amount || 0,
    taxRate: isIncome ? 0 : record.taxRate ?? 3,
    taxDue: isIncome ? 0 : record.taxDue || 0,
  });

  const recalcTaxDue = (amount, rate) => {
    if (isIncome) return 0;
    const a = Number(amount) || 0;
    const r = Number(rate) || 0;
    return Number(((a * r) / 100).toFixed(2));
  };

  const handleAmountChange = (val) => {
    const cleaned = String(val).replace(/[^0-9.]/g, '');
    const amount = parseFloat(cleaned) || 0;
    setForm((p) => {
      const rate = parseFloat(p.taxRate) || (isIncome ? 0 : 3);
      const taxDue = isIncome ? 0 : parseFloat(((amount * rate) / 100).toFixed(2));
      return { ...p, amount, grossSales: amount, taxDue };
    });
  };

  const handleRateChange = (rate) => {
    const cleaned = String(rate).replace(/[^0-9.]/g, '');
    const parsedRate = parseFloat(cleaned) || 0;
    setForm((p) => {
      const amount = parseFloat(p.amount) || 0;
      const taxDue = isIncome ? 0 : parseFloat(((amount * parsedRate) / 100).toFixed(2));
      return { ...p, taxRate: parsedRate, taxDue };
    });
  };

  const handleCOASelect = (account) => {
    if (!account) {
      setForm((p) => ({ ...p, chartOfAccount: null, chartOfAccountName: '' }));
      return;
    }
    const label = `${account.accountNumber || account.accountCode || ''} - ${account.accountName || ''}`.trim();
    setForm((p) => ({ ...p, chartOfAccount: account._id, chartOfAccountName: label }));
  };

  const submit = () => {
    if (!form.month) return alert('Please select month.');
    if (!form.chartOfAccount) return alert('Please select chart of account.');
    if (!form.amount || Number(form.amount) <= 0) return alert('Amount must be > 0.');

    onSave({
      ...record,
      month: Number(form.month),
      chartOfAccount: form.chartOfAccount,
      chartOfAccountName: form.chartOfAccountName,
      amount: Number(form.amount),
      taxRate: isIncome ? 0 : Number(form.taxRate),
      taxDue: isIncome ? 0 : Number(form.taxDue),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg border">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-semibold text-ink">Edit Percentage Tax Record</h3>
          <button onClick={onClose} className="text-ink-body hover:text-ink">✕</button>
        </div>

        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-ink-body mb-1">Month</label>
              <select
                value={form.month}
                onChange={(e) => setForm((p) => ({ ...p, month: Number(e.target.value) }))}
                className="border rounded px-3 py-2 w-full text-sm"
              >
                <option value="">Select</option>
                {monthOptions.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            {!isIncome && (
              <div>
                <label className="block text-xs font-medium text-ink-body mb-1">Tax Rate</label>
                <select
                  value={String(form.taxRate)}
                  onChange={(e) => handleRateChange(Number(e.target.value))}
                  className="border rounded px-3 py-2 w-full text-sm"
                >
                  {taxRates.map((r) => (
                    <option key={r.value} value={String(r.value)}>{r.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-ink-body mb-1">Chart of Account</label>
            <ChartOfAccountsSearch
              value={form.chartOfAccountName}
              onChange={() => {}}
              onSelect={handleCOASelect}
              placeholder="Search chart of accounts..."
              showLinkedBadge={false}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-ink-body mb-1">Amount</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.amount ?? 0}
                onChange={(e) => handleAmountChange(e.target.value)}
                className="w-32 border border-ink-line rounded px-2 py-1 text-sm text-right"
              />
              {Number(form?.amount) > 0 && (
                <div className="text-xs text-ink-body mt-1 text-right">
                  Tax Due ({form?.taxRate}%): {formatCurrency(form?.taxDue)}
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-body mb-1">Tax Due</label>
              <input
                type="number"
                value={form.taxDue ?? 0}
                readOnly
                className="w-32 border border-ink-line rounded px-2 py-1 text-sm text-right bg-ink-canvas cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded hover:bg-ink-canvas">
            Cancel
          </button>
          <button onClick={submit} className="px-4 py-2 text-sm bg-primary-500 text-white rounded hover:bg-primary-600">
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default PercentageTaxEditModal;